/* eslint-disable no-alert */
import React, { useState, useContext, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ChevronLeftIcon } from "@heroicons/react/solid";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import DatePickerField from "components/lib/Shared/DatePickerField";
import {
  addShareAllotmentAsync,
  getShareAllotmentsAsync,
} from "state/slices/shareCapitalAllotment";
import { useToast } from "hooks/useToast";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import Button from "components/lib/Shared/Button";

const changeTypeOptions = [
  { value: "authorization", label: "Authorization" },
  { value: "privateWithMoreThanOneClass", label: "Shares on Offer" },
  {
    value: "unallotedShares",
    label: "Unissued Shares/Existing Authorization/PVT with One Class",
  },
  { value: "shareTransfer", label: "Share Transfer" },
  { value: "transmissionOfShares", label: "Transmission of Shares" },
  { value: "forfeitureOfShares", label: "Forfeiture of Shares" },
  { value: "surrenderOfShares", label: "Surrender of Shares" },
  { value: "sharesBuyback", label: "Shares Buyback" },
  {
    value: "cancelationOfTreasuryShares",
    label: "Cancelation of Treasury Shares",
  },
  {
    value: "saleTransferOfTreasuryShares",
    label: "Sale/transfer of Treasury Shares",
  },
  { value: "sharesRedemption", label: "Shares Redemption" },
  { value: "sharesConsolidation", label: "Shares Consolidation" },
  { value: "sharesSubdivision", label: "Shares Sub-division" },
  {
    value: "noticeToCourt",
    label: "Notice to Court to cancel Special Resolution re Shares Redemption",
  },
  { value: "takeoverOffer", label: "Takeover offer" },
  {
    value: "noticeOfChangeOfName",
    label: "Notice of Change of Name or other Designation of a Class of Shares",
  },
  {
    value: "noticeOfParticulars",
    label: "Notice of Particulars of Variation of Class Rights",
  },
  { value: "solvencyStatement", label: "Solvency Statement" },
  { value: "courtOrder", label: "Court Order" },
];

const processTypeToChangeType = {
  authorization: "AUTHORIZATION",
  existingAuth: "ALLOTMENT",
  privateWithMoreThanOneClass: "AUTHORIZATION-AND-ALLOTMENT",
  unallotedShares: "ALLOTMENT",
  PVTwithOneClass: "ALLOTMENT",
  shareTransfer: "SHARE-TRANSFER",
  transmissionOfShares: "TRANSMISSION-OF-SHARES",
  forfeitureOfShares: "FORFEITURE-OF-SHARES",
  surrenderOfShares: "SURRENDER-OF-SHARES",
  sharesBuyback: "SHARES-BUYBACK",
  cancelationOfTreasuryShares: "CANCELATION-OF-TREASURY-SHARES",
  saleTransferOfTreasuryShares: "SALE-TRANSFER-OF-TREASURY-SHARES",
  sharesRedemption: "SHARES-REDEMPTION",
  sharesConsolidation: "SHARES-CONSOLIDATION",
  sharesSubdivision: "SHARES-SUBDIVISION",
  noticeToCourt: "NOTICE-TO-COURT",
  takeoverOffer: "TAKEOVER-OFFER",
  noticeOfChangeOfName: "NOTICE-OF-CHANGE-OF-NAME",
  noticeOfParticulars: "NOTICE-OF-PARTICULARS",
  solvencyStatement: "SOLVENCY-STATEMENT",
  courtOrder: "COURT-ORDER",
};

const AddSharesOnOffer = ({
  editMode,
  onSubmit,
  onCancel,
  filteredOfficials,
  initialData = {},
  allChangeTypes = [],
  companyId,
  isUpdateMode = false,
  excludedClasses = [], // Array of share classes to exclude from the options
}) => {
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const dispatch = useDispatch();
  const { toast } = useToast(5000);

  const { createdTransactionsIds, setCreatedTransactionsIds } = useContext(
    UpdateCompanyDetailsContext
  );

  const company = useSelector(
    (state) =>
      state.companySlice?.company || state.companyDashboardSlice?.company
  );
  const nominalShares = useSelector(
    (state) => state.companyDashboardSlice?.company?.nominalShares || []
  );
  const proposedShares = useSelector(
    (state) => state.shareAuthorizationSlice?.shares || []
  );

  const effectiveCompanyId = companyId || company?._id;
  const changeType = allChangeTypes[currentFormIndex];
  const isLastForm = currentFormIndex === allChangeTypes.length - 1;

  const isPWMOC = changeType === "privateWithMoreThanOneClass";
  const isUnallotted = changeType === "unallotedShares";
  const isExistingAuth = changeType === "existingAuth";
  const isShareTransfer = changeType === "shareTransfer";
  const isPVTwithOneClass = changeType === "PVTwithOneClass";
  const isForfeiture = changeType === "forfeitureOfShares";

  const calculateShareTotals = (classOfShares) => {
    if (!classOfShares)
      return { totalAllotted: 0, totalPaidUp: 0, totalDistributedAllotted: 0 };

    const n = findNominalByClass(classOfShares);
    const nominalSharesCount = n ? Number(n.sharesNumber || 0) : 0;

    if (!filteredOfficials || filteredOfficials.length === 0)
      return {
        totalAllotted: nominalSharesCount,
        totalPaidUp: 0,
        totalDistributedAllotted: 0,
      };

    const parseNumber = (val) => {
      if (typeof val === "number") return isNaN(val) ? 0 : val;
      if (typeof val === "string") {
        const num = Number(val.replace(/,/g, ""));
        return isNaN(num) ? 0 : num;
      }
      return 0;
    };

    const result = filteredOfficials.reduce(
      (totals, official) => {
        const allotments = official.sharesAllotted || [];
        const classAllotments = allotments.filter(
          (a) => a.classOfShares === classOfShares && a.status === "Current"
        );

        const allottedSum = classAllotments.reduce(
          (s, a) => s + parseNumber(a.allottedShares),
          0
        );

        // paidUpShares is a count — multiply by sharePrice for monetary value
        // or just sum the count depending on what you need
        const paidUpSum = classAllotments.reduce(
          (s, a) => s + parseNumber(a.paidUpShares),
          0
        );

        return {
          totalDistributedAllotted: totals.totalDistributedAllotted + allottedSum,
          totalPaidUp: totals.totalPaidUp + paidUpSum,
        };
      },
      { totalDistributedAllotted: 0, totalPaidUp: 0 }
    );

    return {
      totalAllotted: nominalSharesCount,
      totalPaidUp: result.totalPaidUp,
      totalDistributedAllotted: result.totalDistributedAllotted,
    };
  };

  // Adjusted: Select approvedShares based on changeType
  const approvedShares = useMemo(() => {
    if (isPWMOC) {
      return proposedShares.filter((s) => s.isApproved === false);
    }
    if (isUnallotted || isExistingAuth) {
      return proposedShares.filter((s) => s.isApproved === true);
    }
    return [];
  }, [proposedShares, isPWMOC, isUnallotted, isExistingAuth]);

  const approvedByClass = useMemo(() => {
    const map = new Map();
    approvedShares.forEach((s) => {
      if (!map.has(s.sharesCategory)) map.set(s.sharesCategory, s);
    });
    const result = Array.from(map.values());
    // Filter out excluded classes
    return result.filter(
      (share) => !excludedClasses.includes(share.sharesCategory)
    );
  }, [approvedShares, excludedClasses]);

  const findApprovedByClass = (cls) =>
    approvedByClass.find((a) => a?.sharesCategory === cls);
  const findNominalByClass = (cls) =>
    nominalShares.find((n) => n?.sharesCategory === cls) || null;

  const initialClassOfShares = useMemo(() => {
    if (isPWMOC || isUnallotted || isExistingAuth) {
      const approvedPool =
        Array.isArray(excludedClasses) && excludedClasses.length > 0
          ? approvedByClass.filter((a) =>
            excludedClasses.includes(a.sharesCategory)
          )
          : approvedByClass;

      if (approvedPool.length === 1) return approvedPool[0].sharesCategory;
      if (initialData.classOfShares) return initialData.classOfShares;
      return approvedPool[0]?.sharesCategory || "";
    }

    const nominalPool =
      Array.isArray(excludedClasses) && excludedClasses.length > 0
        ? nominalShares.filter((n) =>
          excludedClasses.includes(n.sharesCategory)
        )
        : nominalShares;

    if (nominalPool.length === 1) return nominalPool[0].sharesCategory;
    if (initialData.classOfShares) return initialData.classOfShares;
    return nominalPool[0]?.sharesCategory || "";
  }, [
    isPWMOC,
    isUnallotted,
    isExistingAuth,
    approvedByClass,
    nominalShares,
    excludedClasses,
    initialData.classOfShares,
  ]);

  const initialSharesAvailable = useMemo(() => {
    const { totalAllotted, totalPaidUp, totalDistributedAllotted } =
      calculateShareTotals(initialClassOfShares);

    if (isPWMOC || isUnallotted || isExistingAuth) {
      const approved = findApprovedByClass(initialClassOfShares);
      const approvedAmount = approved ? Number(approved.sharesNumber || 0) : 0;
      // Allotments should be capped by what's left in the nominal bucket
      const nominalRemaining = Math.max(totalAllotted - totalDistributedAllotted, 0);
      return approvedAmount > 0 ? Math.min(approvedAmount, nominalRemaining) : "";
    }

    if (isShareTransfer) {
      return totalPaidUp;
    }
    if (isForfeiture) {
      const sharesAvailableForForfeiture = totalDistributedAllotted - totalPaidUp;
      return Math.max(sharesAvailableForForfeiture, 0) || "";
    }

    // For PVTwithOneClass and other change types, use nominal remaining shares
    return Math.max(totalAllotted - totalDistributedAllotted, 0) || "";
  }, [
    isPWMOC,
    isUnallotted,
    isExistingAuth,
    initialClassOfShares,
    approvedByClass,
    nominalShares,
    isShareTransfer,
    isForfeiture,
    filteredOfficials,
  ]);

  // Helper function to safely parse date values
  const parseInitialDate = (dateValue) => {
    if (!dateValue) return "";

    // If it's already a Date object, return it if valid
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? "" : dateValue;
    }

    // If it's a string, try to parse it
    if (typeof dateValue === "string") {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? "" : parsed;
    }

    // If it's a number (timestamp), convert it
    if (typeof dateValue === "number") {
      const parsed = new Date(dateValue);
      return isNaN(parsed.getTime()) ? "" : parsed;
    }

    return "";
  };

  const initialValues = {
    classOfShares: initialClassOfShares,
    // Don't include sharesAvailable for PVTwithOneClass
    ...(isPVTwithOneClass ? {} : { sharesAvailable: initialSharesAvailable }),
    sharesOnOffer: initialData.sharesOnOffer || "",
    offerPricePerShare: initialData.offerPricePerShare || "",
    premiumPerShare: initialData.premiumPerShare || "",
    dateOfOffer: parseInitialDate(initialData.dateOfOffer),
  };

  const classOptions = useMemo(() => {
    const baseOptions =
      isPWMOC || isUnallotted || isExistingAuth
        ? approvedByClass
        : nominalShares;

    const baseFilteredByAllowed =
      Array.isArray(excludedClasses) && excludedClasses.length > 0
        ? baseOptions.filter((share) =>
          excludedClasses.includes(share.sharesCategory)
        )
        : baseOptions;

    const filteredByBusinessRules = baseFilteredByAllowed.filter((share) => {
      const cls = share.sharesCategory;
      const { totalPaidUp, totalDistributedAllotted } =
        calculateShareTotals(cls);

      if (isShareTransfer) {
        return totalPaidUp > 0;
      }
      if (isForfeiture) {
        const sharesAvailableForForfeiture = totalDistributedAllotted - totalPaidUp;
        return sharesAvailableForForfeiture > 0;
      }
      return true;
    });

    if (
      initialClassOfShares &&
      !filteredByBusinessRules.some(
        (s) => s.sharesCategory === initialClassOfShares
      )
    ) {
      const fromBase = baseOptions.find(
        (s) => s.sharesCategory === initialClassOfShares
      );
      if (fromBase) {
        return [...filteredByBusinessRules, fromBase];
      }
      return [
        ...filteredByBusinessRules,
        { sharesCategory: initialClassOfShares },
      ];
    }

    return filteredByBusinessRules;
  }, [
    isPWMOC,
    isUnallotted,
    isExistingAuth,
    approvedByClass,
    nominalShares,
    excludedClasses,
    isShareTransfer,
    isForfeiture,
    initialClassOfShares,
    filteredOfficials,
  ]);

  const allotmentSchema = Yup.object({
    classOfShares: Yup.string().required("Required"),
    // Only include sharesAvailable validation if not PVTwithOneClass
    ...(isPVTwithOneClass
      ? {}
      : {
        sharesAvailable: Yup.number()
          .required("Required")
          .min(1, "Must be at least 1"),
      }),
    sharesOnOffer: Yup.number()
      .required("Required")
      .min(1, "Must be at least 1")
      // Only validate against sharesAvailable if not PVTwithOneClass
      .when([], {
        is: () => !isPVTwithOneClass,
        then: (schema) =>
          schema.max(
            Yup.ref("sharesAvailable"),
            "Shares on offer cannot exceed available shares"
          ),
        otherwise: (schema) => schema,
      }),
    offerPricePerShare: Yup.number()
      .min(0, "Cannot be negative")
      .when([], {
        is: () => !isForfeiture,
        then: (schema) => schema.required("Required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    premiumPerShare: Yup.number().min(0, "Cannot be negative"),
    dateOfOffer: Yup.date().required("Required").nullable(),
  });

  if (!effectiveCompanyId) {
    return (
      <div className="p-4 bg-white shadow rounded-lg">
        <p className="text-red-500">
          Error: No company selected. Please select a company and try again.
        </p>
      </div>
    );
  }
  // Error handling for different change types
  if (
    !(isPWMOC || isUnallotted || isExistingAuth) &&
    nominalShares.length === 0
  ) {
    return (
      <div className="p-4 bg-white shadow rounded-lg">
        <p className="text-red-500">
          Error: No share classes available. Please define share classes for the
          company.
        </p>
      </div>
    );
  }
  if (
    (isPWMOC || isUnallotted || isExistingAuth) &&
    approvedByClass.length === 0
  ) {
    return (
      <div className="p-4 bg-white shadow rounded-lg">
        <p className="text-red-500">
          Error: No approved authorizations found. Approve an authorization
          first.
        </p>
      </div>
    );
  }

  const createAllotment = async (values) => {
    const isPVTwithOneClass = changeType === "PVTwithOneClass";

    // For PVTwithOneClass, use nominal shares; for others, use approved shares
    const approved = isPVTwithOneClass
      ? null
      : findApprovedByClass(values.classOfShares);
    const nominal = findNominalByClass(values.classOfShares);

    // Handle nominalId logic based on change type
    let nominalId = null;
    if (isPVTwithOneClass) {
      // For PVTwithOneClass, use nominal share ID directly
      nominalId = nominal?._id || null;
    } else {
      // For other types, prefer authorization's nominal ID, fallback to nominal share ID
      const authNominalId =
        approved?.nominalShareId ||
        approved?.nominalShare?.id ||
        approved?.nominalShare?._id;
      nominalId = authNominalId || nominal?._id || null;
    }

    // Build allotment payload - only include AuthorizationId if it exists
    const allotmentPayload = {
      companyId: effectiveCompanyId,
      classOfShares: values.classOfShares,
      ...(nominalId ? { nominalShareId: nominalId } : {}),
      // For PVTwithOneClass, don't include sharesAvailable
      ...(isPVTwithOneClass
        ? {}
        : { sharesAvailable: Number(values.sharesAvailable) }),
      sharesOnOffer: Number(values.sharesOnOffer),
      offerPricePerShare: Number(values.offerPricePerShare || 0),
      premiumPerShare: Number(values.premiumPerShare || 0),
      dateOfOffer: values.dateOfOffer || null,
      changeType,
      // Only include AuthorizationId if it exists (not null/undefined)
      ...(approved?._id ? { AuthorizationId: approved._id } : {}),
    };
    const res = await dispatch(addShareAllotmentAsync(allotmentPayload));
    if (!addShareAllotmentAsync.fulfilled.match(res)) {
      const msg = res?.payload?.messageText || "Failed to create allotment.";
      throw new Error(msg);
    }

    await dispatch(getShareAllotmentsAsync(effectiveCompanyId));

    const payload = res.payload;
    const allotmentId =
      payload?.allotment?._id ||
      (Array.isArray(payload) ? payload[0]?._id : undefined);

    if (!allotmentId) {
      throw new Error("Could not resolve created allotment ID from response.");
    }

    return { ...(payload?.data || payload), _id: allotmentId };
  };

  const updateExistingTransaction = async (
    allocations,
    remainder,
    changeType,
    responsePayload
  ) => {
    try {
      const fetchAction = await dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId: effectiveCompanyId,
          params: {
            transactionType: "MANUAL",
            changeType: changeType,
          },
        })
      );

      const txs = fetchAction?.payload?.items || [];
      const target = txs.find(
        (tx) => tx.changeType === changeType && tx.status === "DRAFT"
      );
      let updatedShareCapitalChanges;
      let updatePayload;

      if (target?._id) {
        // Existing transaction found, update it
        const shareCapitalChange = target.shareCapitalChanges.find(
          (change) =>
            (responsePayload.AuthorizationId &&
              change.AuthorizationId?.toString() ===
              responsePayload.AuthorizationId) ||
            change.classOfShares === responsePayload.classOfShares
        );

        updatedShareCapitalChanges = shareCapitalChange
          ? [
            ...target.shareCapitalChanges.filter(
              (change) =>
                change.AuthorizationId?.toString() !==
                responsePayload.AuthorizationId &&
                change.classOfShares !== responsePayload.classOfShares
            ),
            {
              ...shareCapitalChange,
              ...responsePayload,
              allocations,
              remainder,
            },
          ]
          : [
            ...target.shareCapitalChanges,
            {
              ...responsePayload,
              allocations,
              remainder,
            },
          ];

        updatePayload = {
          companyId: effectiveCompanyId,
          documentId: target._id,
          formName: "initiateTransactionChange",
          changeType: changeType,
          processType: responsePayload.changeType,
          transactionType: "MANUAL",
          applicationType: "Share Capital",
          shareCapitalChanges: updatedShareCapitalChanges,
          allotments: Array.from(
            new Set([
              ...((target.allotments || []).map(
                (id) => id?.toString?.() || id
              ) || []),
              ...(responsePayload.allotmentId
                ? [responsePayload.allotmentId]
                : []),
            ])
          ),
        };

        const txUpdate = await handleCompanyMaintenanceFormData(
          "existing-request",
          updatePayload,
          effectiveCompanyId
        );

        if (
          txUpdate.type !==
          "transactionChange/editInitiatedTransactionChangeAsync/fulfilled"
        ) {
          const msg =
            txUpdate?.payload?.messageText ||
            "Failed to update transaction with allocations.";
          throw new Error(msg);
        }

        toast("success", "Transaction updated with allotment");
        return txUpdate.payload;
      } else {
        // No existing transaction, create a new one
        const formDetails = {
          companyId: effectiveCompanyId,
          formName: "initiateTransactionChange",
          changeType: changeType,
          processType: responsePayload.changeType,
          transactionType: "MANUAL",
          applicationType: "Share Capital",
          shareCapitalChanges: [
            {
              ...responsePayload,
              allocations,
              remainder,
            },
          ],
          allotments: responsePayload.allotmentId
            ? [responsePayload.allotmentId]
            : [],
        };

        const action = await handleCompanyMaintenanceFormData(
          "new-request",
          formDetails,
          effectiveCompanyId
        );

        const isFulfilled =
          action?.type ===
          "transactionChange/createTransactionChangeAsync/fulfilled";
        const createdTxn =
          action?.payload?.savedTransaction ||
          action?.payload?.data?.transaction ||
          action?.payload;
        if (!isFulfilled || !createdTxn?._id) {
          const msg =
            action?.payload?.messageText || "Failed to create transaction.";
          throw new Error(msg);
        }

        toast("success", "New transaction created with allotment");
        setCreatedTransactionsIds([...createdTransactionsIds, createdTxn._id]);
        return action.payload;
      }
    } catch (error) {
      toast(
        "error",
        error?.message ||
        "Failed to update or create transaction with allotment."
      );
      throw error;
    }
  };

  const createTransaction = async (formDetails) => {
    const action = await handleCompanyMaintenanceFormData(
      "new-request",
      formDetails,
      effectiveCompanyId
    );
    const isFulfilled =
      action?.type ===
      "transactionChange/createTransactionChangeAsync/fulfilled";
    const createdTxn =
      action?.payload?.savedTransaction ||
      action?.payload?.data?.transaction ||
      action?.payload;
    if (!isFulfilled || !createdTxn?._id) {
      const errorMessage =
        action?.payload?.messageText || "Failed to create transaction.";
      throw new Error(errorMessage);
    }
    const createdId = createdTxn?._id;
    if (createdId) {
      setCreatedTransactionsIds([...createdTransactionsIds, createdId]);
    }
    return action.payload;
  };

  const handleSubmit = async (values) => {
    try {
      if (!effectiveCompanyId) throw new Error("Company ID is missing.");

      // If in update mode (edit mode), call onSubmit with the updated values
      if (isUpdateMode || editMode) {
        await onSubmit?.(values);
        return;
      }

      switch (changeType) {
        case "sharesConsolidation":
        case "sharesSubdivision": {
          const n = findNominalByClass(values.classOfShares);
          const par = Number(n?.sharesValue || 0);
          const num = Number(n?.sharesNumber || 0);
          const nominalTotal = par * num;

          const expectedValue = Number(values.expectedShareValue);
          const newNumber = Number(values.newNumberOfShares);
          const tol = 0.01;
          if (Math.abs(expectedValue * newNumber - nominalTotal) > tol) {
            toast(
              "error",
              `Expected value × number of shares must equal ${nominalTotal}`
            );
            return;
          }

          const formDetails = {
            companyId: effectiveCompanyId,
            formName: "initiateTransactionChange",
            changeType: processTypeToChangeType[changeType],
            processType: changeType,
            transactionType: "MANUAL",
            applicationType: "Share Capital",
            shareCapitalChanges: [
              {
                changeType,
                classOfShares: values.classOfShares,
                expectedShareValue: expectedValue,
                newNumberOfShares: newNumber,
                nominalValueTotal: nominalTotal,
                previousParValue: par,
                previousNumberOfShares: num,
              },
            ],
          };

          await createTransaction(formDetails);
          toast("success", "Transaction created successfully");
          if (!isLastForm) setCurrentFormIndex((i) => i + 1);
          else onSubmit?.(formDetails);
          return;
        }

        case "privateWithMoreThanOneClass": {
          // Create the allotment
          const allotment = await createAllotment(values);

          // Prepare the share capital change data
          const shareCapitalChange = {
            changeType,
            classOfShares: values.classOfShares,
            sharesAvailable: Number(values.sharesAvailable),
            sharesOnOffer: Number(values.sharesOnOffer),
            offerPricePerShare: Number(values.offerPricePerShare || 0),
            premiumPerShare: Number(values.premiumPerShare || 0),
            dateOfOffer: values.dateOfOffer || null,
            AuthorizationId:
              findApprovedByClass(values.classOfShares)?._id || null,
            allotmentId: allotment._id,
          };

          // Calculate allocations (modify based on business logic)
          const allocations = []; // TODO: Populate based on shareholder data or business rules
          const remainder =
            Number(values.sharesAvailable) - Number(values.sharesOnOffer);

          if (remainder < 0) {
            toast("error", "Shares on offer cannot exceed available shares.");
            return;
          }

          // Update or create transaction with allotment
          const txUpdate = await updateExistingTransaction(
            allocations,
            remainder,
            processTypeToChangeType[changeType],
            shareCapitalChange
          );

          if (!isLastForm) setCurrentFormIndex((i) => i + 1);
          else onSubmit?.(txUpdate);
          return;
        }

        case "unallotedShares":
        case "existingAuth": {
          const allotment = await createAllotment(values);
          const formDetails = {
            companyId: effectiveCompanyId,
            formName: "initiateTransactionChange",
            changeType: processTypeToChangeType[changeType] || "ALLOTMENT",
            processType: changeType,
            transactionType: "MANUAL",
            applicationType: "Share Capital",
            shareCapitalChanges: [
              {
                changeType,
                classOfShares: values.classOfShares,
                sharesAvailable: Number(values.sharesAvailable),
                sharesOnOffer: Number(values.sharesOnOffer),
                offerPricePerShare: Number(values.offerPricePerShare || 0),
                premiumPerShare: Number(values.premiumPerShare || 0),
                dateOfOffer: values.dateOfOffer || null,
                AuthorizationId:
                  findApprovedByClass(values.classOfShares)?._id || null,
                allotmentId: values._id,
              },
            ],
            allotments: [allotment._id],
          };

          await createTransaction(formDetails);
          toast("success", "Allotment and transaction created successfully");
          if (!isLastForm) setCurrentFormIndex((i) => i + 1);
          else onSubmit?.(formDetails);
          return;
        }

        case "PVTwithOneClass": {
          // Create the allotment first
          const allotment = await createAllotment(values);

          const selectedNominalShare = findNominalByClass(values.classOfShares);
          if (!selectedNominalShare?._id) {
            throw new Error("Selected share class ID is missing.");
          }

          const formDetails = {
            companyId: effectiveCompanyId,
            formName: "initiateTransactionChange",
            changeType: processTypeToChangeType[changeType] || "ALLOTMENT",
            processType: changeType,
            transactionType: "MANUAL",
            applicationType: "Share Capital",
            shareCapitalChanges: [
              {
                changeType,
                classOfShares: values.classOfShares,
                nominalShareId: selectedNominalShare._id,
                // Don't include sharesAvailable for PVTwithOneClass
                sharesOnOffer: Number(values.sharesOnOffer),
                offerPricePerShare: Number(values.offerPricePerShare || 0),
                premiumPerShare: Number(values.premiumPerShare || 0),
                dateOfOffer: values.dateOfOffer || null,
                allotmentId: allotment._id, // Add the allotment ID to the share capital change
              },
            ],
            allotments: [allotment._id], // Add the allotment ID to the transaction
          };

          const transactionAction = await createTransaction(formDetails);
          toast(
            "success",
            "PVT with One Class allotment and transaction created successfully"
          );
          if (!isLastForm) setCurrentFormIndex((i) => i + 1);
          else onSubmit?.(transactionAction);
          return;
        }

        default: {
          const selectedNominalShare = findNominalByClass(values.classOfShares);
          if (!selectedNominalShare?._id) {
            throw new Error("Selected share class ID is missing.");
          }

          const formDetails = {
            companyId: effectiveCompanyId,
            formName: "initiateTransactionChange",
            changeType: processTypeToChangeType[changeType] || "ALLOTMENT",
            processType: changeType,
            transactionType: "MANUAL",
            applicationType: "Share Capital",
            shareCapitalChanges: [
              {
                changeType,
                classOfShares: values.classOfShares,
                nominalShareId: selectedNominalShare._id,
                sharesAvailable: Number(values.sharesAvailable),
                sharesOnOffer: Number(values.sharesOnOffer),
                offerPricePerShare: Number(values.offerPricePerShare || 0),
                premiumPerShare: Number(values.premiumPerShare || 0),
                dateOfOffer: values.dateOfOffer || null,
                AuthorizationId:
                  findApprovedByClass(values.classOfShares)?._id || null,
              },
            ],
          };

          const transactionAction = await createTransaction(formDetails);
          toast("success", "Transaction created successfully");
          if (!isLastForm) setCurrentFormIndex((i) => i + 1);
          else onSubmit?.(transactionAction);
          return;
        }
      }
    } catch (error) {
      const msg =
        error?.message || "An unexpected error occurred. Please try again.";
      toast("error", msg);
      // Re-throw the error when in update mode so parent component can handle it
      if (isUpdateMode || editMode) {
        throw error;
      }
    }
  };

  return (
    <div className="p-4 bg-white shadow rounded-lg">
      <h2 className="text-lg font-semibold mb-2 text-[#31415d]">
        {changeTypeOptions.find((opt) => opt.value === changeType)?.label ||
          "Shares on Offer"}
      </h2>
      {allChangeTypes.length > 1 && (
        <div className="text-sm text-gray-500 mb-2">
          Change Type Selected: {currentFormIndex + 1} of{" "}
          {allChangeTypes.length}
        </div>
      )}
      <hr className="muted mb-4" />

      <Formik
        key={currentFormIndex}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={allotmentSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => {
          const findNominal = (cls) =>
            nominalShares.find((n) => n?.sharesCategory === cls) || null;

          const handleClassOfSharesChange = (e) => {
            const value = e.target.value;
            setFieldValue("classOfShares", value);

            // Only set sharesAvailable if not PVTwithOneClass
            if (!isPVTwithOneClass) {
              const { totalAllotted, totalPaidUp, totalDistributedAllotted } =
                calculateShareTotals(value);

              const nominalRemaining = Math.max(totalAllotted - totalDistributedAllotted, 0);

              if (isPWMOC || isUnallotted || isExistingAuth) {
                const approved = findApprovedByClass(value);
                const approvedAmount = approved ? Number(approved.sharesNumber || 0) : 0;
                setFieldValue(
                  "sharesAvailable",
                  approvedAmount > 0 ? Math.min(approvedAmount, nominalRemaining) : ""
                );
              } else if (isShareTransfer) {
                setFieldValue("sharesAvailable", totalPaidUp);
              } else if (isForfeiture) {
                setFieldValue("sharesAvailable", totalDistributedAllotted - totalPaidUp);
              } else {
                // For other change types (like PVTwithOneClass - though it's filtered above, 
                // this acts as a catch-all for allotments), use nominal remaining shares
                setFieldValue("sharesAvailable", nominalRemaining || "");
              }
            }

            setFieldValue("offerPricePerShare", "");
            setFieldValue("premiumPerShare", "");
            setFieldValue("expectedShareValue", "");
            setFieldValue("newNumberOfShares", "");
          };

          const handleOfferPriceChange = (e) => {
            const offerPriceRaw = e.target.value;
            setFieldValue("offerPricePerShare", offerPriceRaw);

            const offerPrice = Number(offerPriceRaw);
            if (!Number.isFinite(offerPrice)) {
              setFieldValue("premiumPerShare", "");
              return;
            }

            // Determine par value based on context:
            // 1) Prefer nominal share's par value when available
            // 2) Fallback to approved authorization's par value for classes not in nominal shares
            let parValue = 0;
            const n = findNominal(values.classOfShares);
            if (n && typeof n.sharesValue !== "undefined") {
              parValue = Number(n.sharesValue) || 0;
            } else if (isPWMOC || isUnallotted || isExistingAuth) {
              const approved = findApprovedByClass(values.classOfShares);
              if (approved) {
                parValue =
                  Number(
                    // common fields that can carry par value in authorization
                    approved.sharesValue ?? approved.parValue ?? 0
                  ) || 0;
              }
            }

            const premium = Math.max(offerPrice - parValue, 0);
            setFieldValue(
              "premiumPerShare",
              Number.isFinite(premium) ? premium : ""
            );
          };

          return (
            <Form className="space-y-4">
              <div className="grid grid-cols-[1fr_2fr] gap-x-4 items-center">
                <label className="text-sm font-medium text-[#31415d]">
                  Class of Shares
                </label>
                <div>
                  {classOptions.length === 1 || editMode || isUpdateMode ? (
                    <Field
                      type="text"
                      name="classOfShares"
                      readOnly
                      value={
                        classOptions.length === 1
                          ? classOptions[0].sharesCategory
                          : values.classOfShares
                      }
                      className="mt-1 block w-full bg-gray-100 border border-gray-300 rounded-md shadow-sm p-2 text-gray-500"
                    />
                  ) : (
                    <>
                      <Field
                        as="select"
                        name="classOfShares"
                        onChange={handleClassOfSharesChange}
                        value={values.classOfShares}
                        className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm p-2 text-gray-700"
                      >
                        <option value="" disabled>
                          Select class of shares
                        </option>
                        {classOptions.map((item) => (
                          <option key={item._id} value={item.sharesCategory}>
                            {item.sharesCategory}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="classOfShares"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Hide Shares Available field for PVTwithOneClass */}
              {!isPVTwithOneClass && (
                <div className="grid grid-cols-[1fr_2fr] gap-x-4 items-center">
                  <label className="text-sm font-medium text-[#31415d]">
                    Shares Available:
                  </label>
                  <div>
                    <Field
                      type="number"
                      name="sharesAvailable"
                      placeholder="Enter available shares"
                      readOnly={
                        isPWMOC ||
                        isUnallotted ||
                        isExistingAuth ||
                        isShareTransfer ||
                        isForfeiture
                      }
                      className={`mt-1 block w-full border rounded-md shadow-sm p-2 ${isPWMOC ||
                          isUnallotted ||
                          isExistingAuth ||
                          isShareTransfer ||
                          isForfeiture
                          ? "bg-gray-100 border-gray-300 text-gray-500"
                          : "bg-white border-gray-300 text-gray-700"
                        }`}
                    />
                    <ErrorMessage
                      name="sharesAvailable"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-[1fr_2fr] gap-x-4 items-center">
                <label className="text-sm font-medium text-[#31415d]">
                  {isForfeiture ? "Shares to Forfeit:" : "Shares on Offer:"}
                </label>
                <div>
                  <Field
                    type="number"
                    name="sharesOnOffer"
                    placeholder={
                      isForfeiture
                        ? "Enter shares to forfeit"
                        : "Add amount not less than 1"
                    }
                    className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm p-2 text-gray-700"
                  />
                  <ErrorMessage
                    name="sharesOnOffer"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </div>

              {!isForfeiture && (
                <>
                  <div className="grid grid-cols-[1fr_2fr] gap-x-4 items-center">
                    <label className="text-sm font-medium text-[#31415d]">
                      Offer Price Per Share:
                    </label>
                    <div>
                      <Field
                        type="number"
                        name="offerPricePerShare"
                        placeholder="Cannot be less than par value"
                        onChange={handleOfferPriceChange}
                        className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm p-2 text-gray-700"
                      />
                      <ErrorMessage
                        name="offerPricePerShare"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-[1fr_2fr] gap-x-4 items-center">
                    <label className="text-sm font-medium text-[#31415d]">
                      Premium per Share:
                    </label>
                    <Field
                      type="number"
                      name="premiumPerShare"
                      placeholder="[Offer Price - Par Value of Shares]"
                      readOnly
                      className="mt-1 block w-full bg-gray-100 border border-gray-300 rounded-md shadow-sm p-2 text-gray-500"
                    />
                  </div>
                </>
              )}

              <div className="grid grid-cols-[1fr_2fr] gap-x-4 items-center">
                <label className="text-sm font-medium text-[#31415d]">
                  {isForfeiture ? "Date of Forfeiture:" : "Date of Offer:"}
                </label>
                <div>
                  <DatePickerField
                    name="dateOfOffer"
                    placeholderText={
                      isForfeiture
                        ? "Select date of forfeiture"
                        : "Select date of offer"
                    }
                    selected={values.dateOfOffer}
                    onChange={(val) => setFieldValue("dateOfOffer", val)}
                    required
                    errorPath="dateOfOffer"
                    disabled={false}
                  />
                  <ErrorMessage
                    name="dateOfOffer"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex items-center text-[#31415d]"
                >
                  <ChevronLeftIcon className="w-5 h-5 mr-2" />
                  Go back
                </button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  loadingText="Processing..."
                  disabled={isSubmitting}
                  className="flex items-center px-4 py-3 font-semibold text-white rounded bg-[#31415d] disabled:cursor-not-allowed"
                >
                  {isUpdateMode
                    ? "Update Transaction"
                    : isLastForm
                      ? "Save and Proceed"
                      : "Save and Next"}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AddSharesOnOffer;
